import React from "react";
import { Notice } from "../../ui/AdminUi";
import { sessionTitle } from "../common";

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

/**
 * Which of the new product's modules each session of the linked course teaches. One session can cover several modules
 * (a long session with all of them, say), and a student's eCard still carries every individual module of the sessions
 * they attended. Modules in the same choose-one group are alternatives, so ticking one in a session clears the others
 * there. Modules that were already on a session and are not part of this product are left as they are.
 *
 * @param {{ modules: Array<{key: string, name: string, group: string|null}>, sessions: Array<object>,
 *           mapping: Record<string, string[]>, onChange: (sessionId: string|number, keys: string[]) => void,
 *           state: "loading"|"ready"|"needsDraft"|"error", error?: string, onStartDraft: () => void,
 *           busy?: boolean }} props
 */
export default function SessionModuleMapping({ modules, sessions, mapping, onChange, state, error, onStartDraft, busy }) {
  if (state === "loading") return <div className="sub" style={{ fontSize: 13 }}>Loading the course sessions…</div>;
  if (state === "error") return <Notice kind="bad">{error || "The course sessions could not be loaded."}</Notice>;
  if (state === "needsDraft") {
    return (
      <Notice kind="info">
        This course is published, so its sessions cannot change in place. Mapping modules starts a new draft version of the
        course, which stays a draft until you publish it.{" "}
        <button type="button" className="b sec sm" onClick={onStartDraft} disabled={busy}>Start a new draft version</button>
      </Notice>
    );
  }
  if (sessions.length === 0) {
    return <div className="sub" style={{ fontSize: 13 }}>This course has no sessions yet. Add them on its Sessions tab, then map the modules there.</div>;
  }

  const toggle = (sessionId, module, checked) => {
    let next = (mapping[sessionId] || []).filter((k) => k !== module.key);
    if (checked) {
      if (module.group) {
        const siblings = modules.filter((m) => m.group === module.group).map((m) => m.key);
        next = next.filter((k) => !siblings.includes(k));
      }
      next = [...next, module.key];
    }
    onChange(sessionId, next);
  };
  const covered = new Set(Object.values(mapping).flat());
  const unmapped = modules.filter((m) => !covered.has(m.key));

  return (
    <div className="panel ap-mods" data-testid="ap-session-mapping">
      {sessions.map((s, i) => (
        <div key={s.id} className="ap-mod on" data-testid={`ap-session-${s.id}`} style={{ borderTop: i ? "1px solid var(--ga-line)" : 0, flexWrap: "wrap" }}>
          <span className="ap-mod-n" style={{ minWidth: 150 }}>
            <b>{sessionTitle(s, i)}</b>
            <span className="sub" style={{ marginLeft: 8, fontSize: 12 }}>{s.sessionType}{s.startTime ? ` · ${hhmm(s.startTime)}–${hhmm(s.endTime)}` : ""}</span>
          </span>
          <span role="group" aria-label={`Modules in ${sessionTitle(s, i)}`} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {modules.map((m) => (
              <label key={m.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                <input type="checkbox" checked={(mapping[s.id] || []).includes(m.key)}
                  onChange={(e) => toggle(s.id, m, e.target.checked)} />
                {m.name}{m.group ? ` (group ${m.group})` : ""}
              </label>
            ))}
          </span>
        </div>
      ))}
      {unmapped.length > 0 && (
        <div className="ap-warn" style={{ padding: "10px 14px" }} data-testid="ap-unmapped">
          Not in any session yet: {unmapped.map((m) => m.name).join(", ")}. A card only carries the modules of sessions a student attended.
        </div>
      )}
    </div>
  );
}
